/**
 * The lobby (SPEC §2.1, §2.2, §5.5).
 *
 * Roster of license rows as players join; host-only settings for the number of
 * rounds; turn order once it has been randomised; and the start button.
 *
 * The rounds control's ceiling comes from the server (`settings.maxRounds`) and
 * is never recomputed here. Each turn burns one of the questions, so the
 * real limit is `floor(unburned / players)` — and only the server knows how much
 * of the grid a previous game already used up.
 */
import { useParams } from 'react-router-dom';
import {
  DEFAULT_DECK_ID,
  MAX_PLAYERS,
  MAX_ROUNDS,
  MIN_PLAYERS,
  MIN_ROUNDS,
  SAFE_DECK_ID,
  deckSizeFor,
  isSafeMode,
  type PlayerId,
} from '@idgame/protocol';
import {
  useCanStart,
  useDeckReshuffled,
  useHostControls,
  useIsHost,
  useMaxRounds,
  usePlayerCount,
  usePlayers,
  useSettings,
  useTurnOrder,
  type PlayerView,
} from '../../store/index.js';
import { Button, Card, Eyebrow, Notice, Screen } from '../../app/ui.js';
// The game's core visual metaphor, and Agent D's component. There is exactly
// one implementation of it (Wave 3): the lobby's near-duplicate is gone.
import { LicenseCard } from '../../components/LicenseCard.js';
import { HowToPlayButton } from '../../components/HowToPlay.js';
import { RoomQrCode, ShareButton } from './RoomQr.js';

export function LobbyScreen() {
  const { roomCode = '' } = useParams<{ roomCode: string }>();

  const players = usePlayers();
  const playerCount = usePlayerCount();
  const isHost = useIsHost();
  const settings = useSettings();
  const turnOrder = useTurnOrder();
  const canStart = useCanStart();
  const maxRounds = useMaxRounds();
  const deckReshuffled = useDeckReshuffled();
  const { updateSettings, startGame, removePlayer } = useHostControls();

  const rounds = settings?.rounds ?? MIN_ROUNDS;
  const deckId = settings?.deckId ?? DEFAULT_DECK_ID;
  const safeMode = isSafeMode(deckId);

  const ordered = turnOrder.length > 0 ? turnOrder : players;

  return (
    <Screen
      // Share sits in the status row's left slot, where the eyebrow would be.
      eyebrow={<ShareButton roomCode={roomCode} />}
      // …and the rules sit opposite it, in the right slot. The lobby is where
      // the people who were handed a phone thirty seconds ago are sitting, and
      // it is the last screen before the game takes the URL over.
      meta={<HowToPlayButton />}
      title="Scan to join."
      bodyClassName="gap-5 overflow-y-auto"
      footer={
        isHost ? (
          <div className="flex flex-col gap-2">
            {!canStart ? (
              <Notice>
                {playerCount < MIN_PLAYERS
                  ? `${MIN_PLAYERS - playerCount} more ${
                      MIN_PLAYERS - playerCount === 1 ? 'player' : 'players'
                    } needed — below ${MIN_PLAYERS} the guessing is a coin flip.`
                  : playerCount > MAX_PLAYERS
                    ? `That's more than ${MAX_PLAYERS} players.`
                    : 'Waiting for everyone to finish their photo.'}
              </Notice>
            ) : null}
            <Button onClick={startGame} disabled={!canStart}>
              Start the game
            </Button>
          </div>
        ) : (
          <Notice>Waiting for the host to start.</Notice>
        )
      }
    >
      {/*
        SPEC §5.1 — the QR is the hero, the 4-letter code sits beneath it and
        Share is in the corner. It is *here*, on the screen the host actually
        sits on while people arrive, and not behind a "Show QR" button: this is
        the first sixty seconds of the game and getting everyone into the room
        is the only job on screen.

        It used to be a button opening a second, full-bleed `/invite` screen.
        That screen carried the same QR, the same code and a head count, so the
        only thing it added over this one was a "Back to the lobby" button and
        a way to be looking at the wrong screen when the game started. The QR
        keeps the extra centimetre the tap used to buy instead.
      */}
      <div className="flex flex-col items-center gap-3">
        <div className="w-full max-w-[15rem]" data-testid="lobby-qr">
          <RoomQrCode roomCode={roomCode} className="p-4" />
        </div>

        <div className="rounded-card border border-line bg-cream px-4 py-2.5 text-center">
          <Eyebrow>Or type the code</Eyebrow>
          <p className="mt-0.5 text-[2rem] leading-none font-semibold tracking-[0.12em] text-ink">
            {roomCode}
          </p>
        </div>
      </div>

      {deckReshuffled ? <Notice tone="good">The numbered grid has been wiped clean.</Notice> : null}

      {/*
        One roster, not two. Once turn order has been randomised (SPEC §2.1) the
        same list re-sorts into it and picks up rank numbers — a second twelve-row
        list of the same faces underneath the first is the kind of duplication the
        deck's row exists to remove.

        The grid, rather than a flex header row, keeps the heading a sibling of
        the list it names while still leaving room for the turn count.
      */}
      <section className="grid grid-cols-[1fr_auto] items-baseline gap-x-3 gap-y-2">
        <Eyebrow as="h2">
          {turnOrder.length > 0
            ? 'Turn order'
            : `${playerCount} ${playerCount === 1 ? 'player' : 'players'}`}
        </Eyebrow>
        <p
          className="text-right text-[0.6875rem] text-ink-soft"
          aria-live="polite"
          data-testid="time-estimate"
        >
          {playerCount > 0
            ? `${playerCount * rounds} ${playerCount * rounds === 1 ? 'turn' : 'turns'}`
            : `${MIN_PLAYERS}–${MAX_PLAYERS} needed`}
        </p>

        <ol className="col-span-2 flex flex-col gap-1.5">
          {ordered.map((player, index) => (
            <li key={player.id}>
              {/* `PlayerView` is structurally a `LicensePlayer`, so it goes
                  straight in. The lobby-only status and the host's remove
                  control ride along as quiet right-hand meta. */}
              <LicenseCard
                player={player}
                variant="chip"
                rank={turnOrder.length > 0 ? index + 1 : null}
                dimmed={player.removed}
              >
                <LobbyStatus player={player} />
                {isHost && !player.isSelf && !player.removed ? (
                  <button
                    type="button"
                    aria-label={`Remove ${player.name}`}
                    onClick={() => removePlayer(player.id as PlayerId)}
                    /* Ink-soft, not brick: DESIGN.md §1 reserves the error
                       colour for actual failures, and a full room drew twelve
                       red crosses down the edge of the roster. */
                    className="-mr-1 shrink-0 px-2 text-lg leading-none text-ink-soft"
                  >
                    ×
                  </button>
                ) : null}
              </LicenseCard>
            </li>
          ))}
          {ordered.length === 0 ? (
            <li className="rounded-card border border-dashed border-line px-4 py-6 text-center text-[0.8125rem] text-ink-soft">
              Nobody yet. Point them at the code above.
            </li>
          ) : null}
        </ol>
      </section>

      {isHost ? (
        <>
          <RoundsControl
            rounds={rounds}
            maxRounds={maxRounds}
            playerCount={playerCount}
            onChange={(next) => updateSettings({ rounds: next })}
          />
          <SafeModeControl
            safeMode={safeMode}
            onChange={(next) =>
              updateSettings({ deckId: next ? SAFE_DECK_ID : DEFAULT_DECK_ID })
            }
          />
        </>
      ) : (
        /*
          Not host-only information. Which deck the room is about to play is the
          one setting that changes what people are asked to say out loud about
          each other, so everybody sees it before the game starts — a player who
          only finds out at the first question has already agreed to something
          they were not shown.
        */
        <p className="text-[0.8125rem] text-ink-soft">
          {rounds} {rounds === 1 ? 'round' : 'rounds'}
          {safeMode ? ' · Safe mode' : ''}
        </p>
      )}
    </Screen>
  );
}

/* -------------------------------------------------------------------------- */
/* Roster status                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The lobby-only meta on a player row — quiet right-aligned type, not a badge.
 * Deliberately short: the row already says "You" / "offline" for the two cases
 * it knows about, so this covers only what the lobby adds — who is host, who
 * has not taken a photo, and who the host has removed (SPEC §5.4).
 */
function LobbyStatus({ player }: { player: PlayerView }) {
  const label = player.removed
    ? 'Removed'
    : !player.hasPhoto
      ? 'No photo'
      : player.isHost
        ? 'Host'
        : null;
  if (!label) return null;

  return <span className="shrink-0 text-[0.6875rem] text-ink-soft">{label}</span>;
}

/* -------------------------------------------------------------------------- */
/* Rounds control (SPEC §2.2)                                                  */
/* -------------------------------------------------------------------------- */

function RoundsControl({
  rounds,
  maxRounds,
  playerCount,
  onChange,
}: {
  rounds: number;
  maxRounds: number;
  playerCount: number;
  onChange: (rounds: number) => void;
}) {
  const clamped = Math.min(rounds, maxRounds);
  const step = 'rounded-btn h-11 w-11 shrink-0 border border-line bg-cream-deep text-xl font-semibold text-ink disabled:border-transparent disabled:bg-disabled disabled:text-ink-soft/45';

  return (
    <Card>
      <Eyebrow>Rounds</Eyebrow>

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          aria-label="Fewer rounds"
          disabled={clamped <= MIN_ROUNDS}
          onClick={() => onChange(Math.max(MIN_ROUNDS, clamped - 1))}
          className={step}
        >
          −
        </button>
        <output className="flex-1 text-center text-[2rem] leading-none font-semibold text-ink tabular-nums">
          {clamped}
        </output>
        <button
          type="button"
          aria-label="More rounds"
          disabled={clamped >= maxRounds}
          onClick={() => onChange(Math.min(maxRounds, clamped + 1))}
          className={step}
        >
          +
        </button>
      </div>

      {maxRounds < MAX_ROUNDS && playerCount > 0 ? (
        <p className="mt-3 text-[0.6875rem] text-ink-soft">
          Capped at {maxRounds} with {playerCount} players — that is how many questions are left.
        </p>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Safe mode (SPEC §3.1 — the Work-Safe deck)                                  */
/* -------------------------------------------------------------------------- */

/**
 * The host's switch between the two decks.
 *
 * It is a deck swap, not a filter over one deck: safe mode plays the work-safe
 * questions renumbered as their own board, which is why the count under the
 * label changes with the switch rather than staying at 201 with holes in it.
 * The number matters to the host for a second reason — fewer questions is a
 * lower ceiling on rounds — and the server re-sends `maxRounds` with the deck,
 * so the stepper above tightens on its own.
 *
 * Lobby-only, like every other setting: the server refuses `update_settings`
 * once the game has started, so a room cannot change what it is halfway through.
 */
function SafeModeControl({
  safeMode,
  onChange,
}: {
  safeMode: boolean;
  onChange: (safeMode: boolean) => void;
}) {
  const deckSize = deckSizeFor(safeMode ? SAFE_DECK_ID : DEFAULT_DECK_ID);

  return (
    <Card>
      {/* The label *is* the control: the whole row is the switch's hit area,
          which is the only way a 44px target survives on a phone. */}
      <label className="flex cursor-pointer items-center gap-3" data-testid="safe-mode">
        <span className="flex-1">
          <Eyebrow as="span">Safe mode</Eyebrow>
          <span className="mt-1 block text-[0.8125rem] leading-snug text-ink-soft">
            {safeMode
              ? `${deckSize} questions. Nothing about sex or drugs — still mean.`
              : `All ${deckSize} questions, including the filthy ones.`}
          </span>
        </span>

        <input
          type="checkbox"
          role="switch"
          checked={safeMode}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
          data-testid="safe-mode-toggle"
        />
        {/*
          A real checkbox behind a drawn track, rather than a div with an
          onClick: it arrives with the switch role, the checked state, keyboard
          focus and the label association already correct.

          The track takes its focus ring from `peer-*`, which is a sibling
          selector and so reaches it. The knob's position does *not*: it is a
          descendant of the track rather than a sibling of the input, which
          `peer-checked:` cannot see. It reads `safeMode` directly instead —
          the same state the input is controlled by, so the two cannot part.
        */}
        <span
          aria-hidden
          className={[
            'relative h-7 w-12 shrink-0 rounded-full transition',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-ink/30',
            safeMode ? 'bg-green' : 'bg-disabled',
          ].join(' ')}
        >
          <span
            className={[
              'absolute top-1 left-1 h-5 w-5 rounded-full bg-cream shadow-sm transition-transform',
              safeMode ? 'translate-x-5' : 'translate-x-0',
            ].join(' ')}
          />
        </span>
      </label>
    </Card>
  );
}
